/** Small formatting helpers shared across the console. */

/** Up to two letters from a name, for the avatar tiles. */
export const initials = (name?: string): string =>
  (name || '?')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase() ?? '')
    .join('') || '?';

const dateFmt = new Intl.DateTimeFormat('en-IN', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
});

export const formatDate = (value?: string | Date | null): string =>
  value ? dateFmt.format(new Date(value)) : 'No date';

export const formatDateTime = (value?: string | Date | null): string =>
  value
    ? `${dateFmt.format(new Date(value))}, ${new Date(value).toLocaleTimeString('en-IN', {
        hour: '2-digit',
        minute: '2-digit',
      })}`
    : 'Not set';

/** "2 days ago" for anything recent, an absolute date beyond a month. */
export function relativeDate(value?: string | Date | null): string {
  if (!value) return 'Not set';
  const then = new Date(value).getTime();
  const days = Math.floor((Date.now() - then) / 86_400_000);
  if (days <= 0) {
    const hours = Math.floor((Date.now() - then) / 3_600_000);
    if (hours <= 0) return 'Just now';
    return hours === 1 ? '1 hour ago' : `${hours} hours ago`;
  }
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  if (days < 14) return '1 week ago';
  if (days < 31) return `${Math.floor(days / 7)} weeks ago`;
  return dateFmt.format(new Date(value));
}

/** Indian digit grouping, matching the mobile app's money presentation. */
export const inr = (amount: number): string => `₹${Number(amount || 0).toLocaleString('en-IN')}`;

/** Compact rupee for dashboard tiles: ₹8.42L, ₹1.01Cr. */
export function inrCompact(amount: number): string {
  const n = Number(amount || 0);
  if (n >= 1_00_00_000) return `₹${(n / 1_00_00_000).toFixed(2)}Cr`;
  if (n >= 1_00_000) return `₹${(n / 1_00_000).toFixed(2)}L`;
  if (n >= 1_000) return `₹${(n / 1_000).toFixed(1)}K`;
  return inr(n);
}

export const pluralise = (count: number, one: string, many = `${one}s`): string =>
  `${count} ${count === 1 ? one : many}`;
